/**
 * Modal de detalle de un pase del Greenie Board — Fase 5g.4 (vista) +
 * 5g.5d (edición admin).
 *
 * Al pinchar un cuadro del tablero se abre este modal, con dos modos:
 *
 *  - VISTA (cualquiera): por intento, calificación + cable y la nota del
 *    LSO decodificada código a código (`decodeLsoComment`), agrupada por
 *    fase del approach. Los códigos sin reconocer salen en gris.
 *
 *  - EDICIÓN (solo admin): cambia grade / cable / comentario de cada
 *    intento, borra intentos y añade uno nuevo — el "split" de un bolter
 *    seguido de trap que DCS plegó en un solo evento. Guarda contra
 *    `PATCH/POST/DELETE /api/greenie-board/landings` y refresca el
 *    tablero con el callback `onSaved`.
 *
 * Un intento nuevo se inserta arriba (el más antiguo del pase) y se ancla
 * "antes" del primer intento existente: así el alta hereda en el servidor
 * piloto/misión/etc. de ese hermano. Se permite un único intento nuevo
 * por sesión de edición, y nunca se borra el último intento existente
 * (dejaría el pase vacío y el alta sin hermano).
 */

import {
  decodeLsoComment,
  LSO_PHASE_LABELS,
  LSO_PHASE_ORDER,
  LSO_SEVERITY_LABELS,
  type DecodedLsoCode,
  type GreenieAttempt,
  type GreeniePass,
} from '@51esc/shared';
import { createLanding, deleteLanding, patchLanding } from './api.js';
import { EDITABLE_GRADES, legendShort } from './markup.js';

/** Opciones del modal — quién lo abre y qué puede hacer. */
export interface PassModalOptions {
  /** `true` si el usuario es admin/superadmin: habilita el modo edición. */
  canEdit?: boolean;
  /** Callback tras un guardado correcto — el mount recarga el tablero. */
  onSaved?: () => void | Promise<void>;
}

/** Un intento en el editor — `id` es `null` si es un alta pendiente. */
interface EditRow {
  id: string | null;
  grade: string;
  wire: number | null;
  rawComment: string;
}

/** Snapshot de los valores originales de un intento, para detectar cambios. */
interface RowSnapshot {
  grade: string;
  wire: number | null;
  rawComment: string;
}

/** Estado del modo edición. `null` cuando el modal está en modo vista. */
interface EditState {
  rows: EditRow[];
  /** IDs de intentos existentes marcados para borrar. */
  deletedIds: string[];
  /** Valores originales por ID, para guardar solo lo que cambió. */
  original: Map<string, RowSnapshot>;
  /** `true` mientras se guarda — deshabilita los botones. */
  saving: boolean;
  /** Mensaje del último error de guardado, o `null`. */
  error: string | null;
}

let activeOverlay: HTMLElement | null = null;
let lastFocused: HTMLElement | null = null;
let currentPass: GreeniePass | null = null;
let currentOpts: PassModalOptions = {};
let editState: EditState | null = null;

/** Abre el modal de detalle para un pase del tablero. */
export function openPassModal(
  pass: GreeniePass,
  opts: PassModalOptions = {},
): void {
  closePassModal();
  lastFocused = document.activeElement as HTMLElement | null;
  currentPass = pass;
  currentOpts = opts;
  editState = null;

  const overlay = document.createElement('div');
  overlay.className = 'gb-modal-overlay';
  overlay.innerHTML = `
    <div
      class="gb-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Detalle del pase: ${esc(pass.missionName)}"
      tabindex="-1"
    ></div>
  `;
  document.body.appendChild(overlay);
  activeOverlay = overlay;

  overlay.addEventListener('click', onClick);
  overlay.addEventListener('input', onInput);
  document.addEventListener('keydown', onKeydown);

  repaint();
  overlay.querySelector<HTMLElement>('.gb-modal')?.focus();
}

/** Cierra el modal si hay uno abierto y restaura el foco. */
export function closePassModal(): void {
  if (!activeOverlay) return;
  document.removeEventListener('keydown', onKeydown);
  activeOverlay.remove();
  activeOverlay = null;
  currentPass = null;
  currentOpts = {};
  editState = null;
  lastFocused?.focus();
  lastFocused = null;
}

/** Repinta el contenido del modal según el modo (vista / edición). */
function repaint(): void {
  if (!activeOverlay || !currentPass) return;
  const modal = activeOverlay.querySelector<HTMLElement>('.gb-modal');
  if (!modal) return;
  modal.innerHTML = editState
    ? renderEdit(currentPass, editState)
    : renderView(currentPass, currentOpts);
}

// ─── Eventos ───────────────────────────────────────────────────────────

function onClick(ev: MouseEvent): void {
  const t = ev.target as HTMLElement;
  if (t === activeOverlay || t.closest('[data-close]')) {
    closePassModal();
    return;
  }
  if (t.closest('[data-edit]')) {
    enterEditMode();
    return;
  }
  if (t.closest('[data-cancel]')) {
    editState = null;
    repaint();
    return;
  }
  if (t.closest('[data-add]')) {
    addRow();
    return;
  }
  const del = t.closest<HTMLElement>('[data-del]');
  if (del) {
    removeRow(Number(del.dataset.del ?? ''));
    return;
  }
  if (t.closest('[data-save]')) {
    void save();
  }
}

function onInput(ev: Event): void {
  if (!editState) return;
  const el = ev.target as HTMLElement;
  const field = el.dataset.field;
  const rowIdx = el.dataset.row;
  if (!field || rowIdx === undefined) return;
  const row = editState.rows[Number(rowIdx)];
  if (!row) return;
  if (field === 'grade' && el instanceof HTMLSelectElement) {
    row.grade = el.value;
  } else if (field === 'wire' && el instanceof HTMLSelectElement) {
    row.wire = el.value === '' ? null : Number(el.value);
  } else if (field === 'comment' && el instanceof HTMLTextAreaElement) {
    row.rawComment = el.value;
  }
}

function onKeydown(ev: KeyboardEvent): void {
  if (ev.key !== 'Escape') return;
  if (editState) {
    // En edición, Escape vuelve a la vista (salvo mientras se guarda).
    if (editState.saving) return;
    editState = null;
    repaint();
  } else {
    closePassModal();
  }
}

// ─── Modo edición: transiciones y mutación de estado ───────────────────

function enterEditMode(): void {
  if (!currentPass) return;
  const original = new Map<string, RowSnapshot>();
  const rows: EditRow[] = currentPass.attempts.map((a) => {
    original.set(a.id, {
      grade: a.grade,
      wire: a.wire,
      rawComment: a.rawComment,
    });
    return { id: a.id, grade: a.grade, wire: a.wire, rawComment: a.rawComment };
  });
  editState = { rows, deletedIds: [], original, saving: false, error: null };
  repaint();
}

/** Añade un intento nuevo (uno como máximo) arriba del todo. */
function addRow(): void {
  if (!editState) return;
  if (editState.rows.some((r) => r.id === null)) return;
  // Va arriba (el más antiguo) — el caso típico es el bolter previo al
  // trap. Por defecto `B` sin cable; el admin lo ajusta.
  editState.rows.unshift({ id: null, grade: 'B', wire: null, rawComment: '' });
  repaint();
}

/** Quita un intento. Los existentes se marcan para borrar en el servidor. */
function removeRow(idx: number): void {
  if (!editState) return;
  const row = editState.rows[idx];
  if (!row) return;
  if (row.id !== null) {
    // No se borra el último intento existente: dejaría el pase vacío y
    // un posible alta nueva sin intento de referencia.
    const existing = editState.rows.filter((r) => r.id !== null).length;
    if (existing <= 1) return;
    editState.deletedIds.push(row.id);
  }
  editState.rows.splice(idx, 1);
  repaint();
}

/** Guarda los cambios: borra, edita y crea contra el API; luego refresca. */
async function save(): Promise<void> {
  const state = editState;
  if (!state || state.saving) return;
  state.saving = true;
  state.error = null;
  repaint();

  try {
    // 1) Borrados de intentos existentes.
    for (const id of state.deletedIds) {
      const r = await deleteLanding(id);
      if (!r.ok) throw new Error(r.message);
    }
    // 2) Ediciones de intentos existentes que hayan cambiado.
    for (const row of state.rows) {
      if (row.id === null) continue;
      const orig = state.original.get(row.id);
      if (
        orig &&
        orig.grade === row.grade &&
        orig.wire === row.wire &&
        orig.rawComment === row.rawComment
      ) {
        continue;
      }
      const r = await patchLanding(row.id, {
        grade: row.grade,
        wire: row.wire,
        rawComment: row.rawComment,
      });
      if (!r.ok) throw new Error(r.message);
    }
    // 3) Alta del intento nuevo (uno como máximo), anclado "antes" del
    //    primer intento existente que sobreviva.
    const newRow = state.rows.find((r) => r.id === null);
    if (newRow) {
      const sibling = state.rows.find((r) => r.id !== null);
      if (!sibling?.id) {
        throw new Error('No hay un intento de referencia para el nuevo.');
      }
      const comment = newRow.rawComment.trim();
      const r = await createLanding({
        siblingAttemptId: sibling.id,
        position: 'before',
        grade: newRow.grade,
        wire: newRow.wire,
        rawComment: comment === '' ? undefined : newRow.rawComment,
      });
      if (!r.ok) throw new Error(r.message);
    }
  } catch (e) {
    if (editState) {
      editState.saving = false;
      editState.error = e instanceof Error ? e.message : 'No se pudo guardar.';
      repaint();
    }
    return;
  }

  // Éxito: cerramos y dejamos que el mount recargue el tablero.
  const onSaved = currentOpts.onSaved;
  closePassModal();
  if (onSaved) await onSaved();
}

// ─── Render: cabecera común ────────────────────────────────────────────

function renderHead(pass: GreeniePass): string {
  return `
    <header class="gb-modal-head">
      <div class="gb-modal-titles">
        <h2 class="gb-modal-title">${esc(pass.missionName)}</h2>
        <p class="gb-modal-date">${esc(formatDate(pass.dateIso))}</p>
      </div>
      <button
        type="button"
        class="gb-modal-close"
        data-close
        aria-label="Cerrar"
      >×</button>
    </header>
  `;
}

// ─── Render: modo vista ────────────────────────────────────────────────

function renderView(pass: GreeniePass, opts: PassModalOptions): string {
  const total = pass.attempts.length;
  const attempts = pass.attempts
    .map((a, i) => renderViewAttempt(a, i, total))
    .join('');
  const editBtn = opts.canEdit
    ? `<button type="button" class="gb-btn gb-btn--edit" data-edit>✎ Editar apontaje</button>`
    : '';
  return `
    ${renderHead(pass)}
    <div class="gb-modal-body">${attempts}</div>
    <footer class="gb-modal-foot">
      <span class="gb-modal-foot-note">
        Decodificación de la nota del LSO. Los códigos sin reconocer se
        muestran en gris — no se interpreta su significado.
      </span>
      ${editBtn}
    </footer>
  `;
}

function renderViewAttempt(
  a: GreenieAttempt,
  idx: number,
  total: number,
): string {
  const decoded = decodeLsoComment(a.rawComment);
  const wireText = a.wire !== null ? ` · cable ${String(a.wire)}` : '';
  const heading =
    total > 1 ? `Intento ${String(idx + 1)} de ${String(total)}` : 'Pase';
  const edited = a.editedAt
    ? `<p class="gb-attempt-edited">✎ Corregido a mano${
        a.editedByCallsign ? ` por ${esc(a.editedByCallsign)}` : ''
      } · ${esc(formatDate(a.editedAt))}</p>`
    : '';
  return `
    <section class="gb-modal-attempt">
      <h3 class="gb-modal-attempt-head">
        <span class="gb-modal-attempt-n">${esc(heading)}</span>
        <span class="gb-modal-grade">${esc(a.grade)}${esc(wireText)}</span>
      </h3>
      ${edited}
      ${renderCodeGroups(decoded.codes, decoded.note)}
      <details class="gb-modal-raw">
        <summary>Nota LSO original</summary>
        <code>${esc(a.rawComment)}</code>
      </details>
    </section>
  `;
}

/**
 * Agrupa los códigos por fase del approach y los renderiza en orden.
 *
 * `note` solo llega en las notas sin calificar (los `NC`): ahí no hay
 * códigos que agrupar, sino el motivo por el que el LSO no evaluó el pase.
 * Decirlo es más útil que un "no anotó desviaciones" que suena a pase
 * limpio, que es justo lo contrario de lo que pasó.
 */
function renderCodeGroups(codes: DecodedLsoCode[], note: string | null): string {
  if (note !== null) {
    return `<p class="gb-modal-nocodes">El LSO no calificó el pase: ${esc(note)}.</p>`;
  }
  if (codes.length === 0) {
    return '<p class="gb-modal-nocodes">El LSO no anotó desviaciones.</p>';
  }
  return LSO_PHASE_ORDER.map((phase) => {
    const inPhase = codes.filter((c) => c.phase === phase);
    if (inPhase.length === 0) return '';
    return `
      <div class="gb-modal-phase">
        <h4 class="gb-modal-phase-label">${esc(LSO_PHASE_LABELS[phase])}</h4>
        <ul class="gb-modal-codes">${inPhase.map(renderCode).join('')}</ul>
      </div>
    `;
  }).join('');
}

function renderCode(c: DecodedLsoCode): string {
  if (!c.decoded) {
    return `
      <li class="gb-code gb-code--unknown">
        <span class="gb-code-raw">${esc(c.raw)}</span>
        <span class="gb-code-text">código sin decodificar</span>
      </li>
    `;
  }
  return `
    <li class="gb-code gb-code--${c.severity}">
      <span class="gb-code-raw">${esc(c.raw)}</span>
      <span class="gb-code-text">${esc(c.text ?? '')}</span>
      <span class="gb-code-sev">${esc(LSO_SEVERITY_LABELS[c.severity])}</span>
    </li>
  `;
}

// ─── Render: modo edición ──────────────────────────────────────────────

function renderEdit(pass: GreeniePass, state: EditState): string {
  const existing = state.rows.filter((r) => r.id !== null).length;
  const hasNew = state.rows.some((r) => r.id === null);
  const rows = state.rows
    .map((r, i) => renderEditRow(r, i, existing))
    .join('');
  const addBtn = hasNew
    ? ''
    : `<button type="button" class="gb-edit-add" data-add>+ Añadir intento (p. ej. un bolter previo)</button>`;
  const error = state.error
    ? `<p class="gb-edit-error" role="alert">${esc(state.error)}</p>`
    : '';
  const disabled = state.saving ? 'disabled' : '';
  return `
    ${renderHead(pass)}
    <div class="gb-modal-body gb-edit-body">
      <p class="gb-edit-intro">
        Corrige la calificación, el cable o la nota de cada intento. Para
        partir un apontaje (un bolter y luego el trap) añade un intento.
      </p>
      ${rows}
      ${addBtn}
    </div>
    <footer class="gb-modal-foot gb-edit-foot">
      ${error}
      <div class="gb-edit-actions">
        <button
          type="button"
          class="gb-btn gb-btn--ghost"
          data-cancel
          ${disabled}
        >Cancelar</button>
        <button
          type="button"
          class="gb-btn gb-btn--save"
          data-save
          ${disabled}
        >${state.saving ? 'Guardando…' : 'Guardar cambios'}</button>
      </div>
    </footer>
  `;
}

function renderEditRow(row: EditRow, idx: number, existing: number): string {
  const isNew = row.id === null;
  const heading = isNew ? 'Intento nuevo' : `Intento ${String(idx + 1)}`;
  // El último intento existente no se puede borrar.
  const delDisabled = !isNew && existing <= 1 ? 'disabled' : '';
  const delLabel = isNew ? 'Quitar' : 'Borrar';
  const placeholder = isNew
    ? 'Opcional — si se deja vacía se generará «LSO: GRADE:…»'
    : '';
  return `
    <div class="gb-edit-row${isNew ? ' gb-edit-row--new' : ''}">
      <div class="gb-edit-row-head">
        <span class="gb-edit-row-n">${esc(heading)}</span>
        <button
          type="button"
          class="gb-edit-del"
          data-del="${String(idx)}"
          ${delDisabled}
        >${delLabel}</button>
      </div>
      <div class="gb-edit-fields">
        <label class="gb-edit-field">
          <span class="gb-edit-label">Calificación</span>
          ${renderGradeSelect(row.grade, idx)}
        </label>
        <label class="gb-edit-field gb-edit-field--wire">
          <span class="gb-edit-label">Cable</span>
          ${renderWireSelect(row.wire, idx)}
        </label>
      </div>
      <label class="gb-edit-field">
        <span class="gb-edit-label">Nota LSO</span>
        <textarea
          class="gb-edit-input gb-edit-comment"
          rows="2"
          data-field="comment"
          data-row="${String(idx)}"
          placeholder="${esc(placeholder)}"
        >${esc(row.rawComment)}</textarea>
      </label>
    </div>
  `;
}

function renderGradeSelect(grade: string, idx: number): string {
  // Un grade fuera del catálogo (DCS pudo emitir algo raro) se conserva
  // como opción propia para no perderlo al editar otro campo.
  const extra = EDITABLE_GRADES.includes(grade)
    ? ''
    : `<option value="${esc(grade)}" selected>${esc(grade)} · (sin tabular)</option>`;
  const opts = EDITABLE_GRADES.map((g) => {
    const sel = g === grade ? ' selected' : '';
    return `<option value="${esc(g)}"${sel}>${esc(g)} · ${esc(legendShort(g))}</option>`;
  }).join('');
  return `<select class="gb-edit-input" data-field="grade" data-row="${String(idx)}">${extra}${opts}</select>`;
}

function renderWireSelect(wire: number | null, idx: number): string {
  const opt = (val: string, label: string, on: boolean): string =>
    `<option value="${val}"${on ? ' selected' : ''}>${label}</option>`;
  const opts = [
    opt('', '— (sin cable)', wire === null),
    opt('1', 'Cable 1', wire === 1),
    opt('2', 'Cable 2', wire === 2),
    opt('3', 'Cable 3', wire === 3),
    opt('4', 'Cable 4', wire === 4),
  ].join('');
  return `<select class="gb-edit-input" data-field="wire" data-row="${String(idx)}">${opts}</select>`;
}

// ─── Helpers ───────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
